import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

interface PageTransitionProps {
  children: ReactNode;
}

// Major sections get the branded splash; subsections get a quick fade
const MAJOR_ROUTES = ['/commissions', '/book-of-business', '/points'];

function isMajorRoute(path: string): boolean {
  return MAJOR_ROUTES.some(r => path === r || path === r + '/');
}

function BrandedSplash({ onComplete, isDark }: { onComplete: () => void; isDark: boolean }) {
  useEffect(() => {
    const timer = setTimeout(onComplete, 600);
    return () => clearTimeout(timer);
  }, [onComplete]);

  const bg = isDark
    ? 'linear-gradient(135deg, #111827 0%, #1D2550 100%)'
    : 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)';
  const glowColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)';
  const shimmerColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.08)';
  const logoFilter = isDark ? 'brightness(0) invert(1)' : 'brightness(0) invert(0.3)';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: bg, pointerEvents: 'none',
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1.2, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          position: 'absolute', width: 300, height: 300, borderRadius: '50%',
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
        }}
      />
      <motion.img
        src="/wfg-logo.svg"
        alt=""
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 1.1, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: 120, height: 'auto', position: 'relative', zIndex: 1, filter: logoFilter }}
      />
      <motion.div
        initial={{ x: '-100%', opacity: 0 }}
        animate={{ x: '100%', opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15, ease: 'easeInOut' }}
        style={{
          position: 'absolute', bottom: '45%', left: 0, width: '40%', height: 1,
          background: `linear-gradient(90deg, transparent, ${shimmerColor}, transparent)`,
        }}
      />
    </motion.div>
  );
}

export function PageTransition({ children }: PageTransitionProps) {
  const { effectiveTheme } = useTheme();
  const location = useLocation();
  const isDark = effectiveTheme === 'dark';
  const major = isMajorRoute(location.pathname);

  const [showSplash, setShowSplash] = useState(major);
  const [showContent, setShowContent] = useState(!major);

  useEffect(() => {
    if (major) {
      setShowSplash(true);
      setShowContent(false);
    } else {
      setShowSplash(false);
      setShowContent(true);
    }
  }, [major]);

  const handleSplashComplete = () => {
    setShowSplash(false);
    setShowContent(true);
  };

  // Subsections: quick fade-in, no splash
  if (!major) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  // Major sections: branded splash then content
  return (
    <>
      <AnimatePresence>
        {showSplash && <BrandedSplash onComplete={handleSplashComplete} isDark={isDark} />}
      </AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={showContent ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </>
  );
}
